import type { FitnessValues, Sport, WeekDay } from '@painpal/contracts';
import { SPORTS, weekEnd } from '@painpal/core';
import type { SportFilter } from '../../api/keys';
import { formatDayMonth, formatHoursMinutes, formatKmValue, formatSigned, formatWeekdayDay, formatWhole } from '../../lib/format';
import { sportCount } from '../../lib/sports';
import { filterLabel, pluralNoun } from './sportFilterModel';

export type WeekMetric = 'distance' | 'duration' | 'load';
export type DayFigures = Pick<WeekDay, 'distanceM' | 'movingS' | 'load' | 'activities' | 'noLoad'>;
type Figures = Pick<WeekDay, 'distanceM' | 'movingS' | 'load'>;

export const WEEK_METRICS: readonly { value: WeekMetric; label: string }[] = [
  { value: 'distance', label: 'DISTANCE' },
  { value: 'duration', label: 'DURATION' },
  { value: 'load', label: 'LOAD' },
];
// The same for every week so weeks compare by eye; a bigger day becomes that week's scale so no bar overflows.
export const FIXED_SCALE: Record<WeekMetric, number> = { load: 250, duration: 4 * 3600, distance: 120_000 };
export const TRACK_PX = { desktop: 140, phone: 128 } as const;
export const trackPx = (isPhone: boolean): number => TRACK_PX[isPhone ? 'phone' : 'desktop'];
const MIN_BAR_PX = 3;
// After the selected metric, the totals keep this order.
const ORDER: readonly WeekMetric[] = ['duration', 'distance', 'load'];

const VALUE: Record<WeekMetric, (figures: Figures) => number> = {
  distance: (f) => f.distanceM,
  duration: (f) => f.movingS,
  load: (f) => f.load,
};
const SHORT: Record<WeekMetric, (n: number) => string> = { distance: formatKmValue, duration: formatHoursMinutes, load: formatWhole };
const LONG: Record<WeekMetric, (n: number) => string> = {
  distance: (m) => `${formatKmValue(m)} KM`,
  duration: formatHoursMinutes,
  load: (n) => `${formatWhole(n)} LOAD`,
};

export const metricValue = (figures: Figures, metric: WeekMetric): number => VALUE[metric](figures);

export function weekTotals(days: readonly WeekDay[]): DayFigures {
  return days.reduce((t, d) => ({
    distanceM: t.distanceM + d.distanceM,
    movingS: t.movingS + d.movingS,
    load: t.load + d.load,
    activities: t.activities + d.activities,
    noLoad: t.noLoad + d.noLoad,
  }), { distanceM: 0, movingS: 0, load: 0, activities: 0, noLoad: 0 });
}

export function scaleFor(days: readonly WeekDay[], metric: WeekMetric): number {
  return Math.max(FIXED_SCALE[metric], ...days.map(VALUE[metric]));
}

export function barPx(value: number, scale: number, track: number): number {
  return value === 0 ? 0 : Math.max(MIN_BAR_PX, Math.round((value / scale) * track));
}

export const barLabel = (value: number, metric: WeekMetric): string => (value === 0 ? '—' : SHORT[metric](value));

export interface Segment { sport: Sport; value: number }

// Bottom up in SPORTS order; a sport with nothing in this metric (a swim's Load of 0) leaves no segment.
export function daySegments(day: Pick<WeekDay, 'bySport'>, metric: WeekMetric): Segment[] {
  return SPORTS.flatMap((sport) => {
    const figures = day.bySport[sport];
    const value = figures === undefined ? 0 : VALUE[metric](figures);
    return value > 0 ? [{ sport, value }] : [];
  });
}

export const daySports = (day: Pick<WeekDay, 'bySport'>): Sport[] => SPORTS.filter((sport) => day.bySport[sport] !== undefined);

export function weekRangeLabel(start: string): string {
  const end = weekEnd(start);
  const from = start.slice(0, 7) === end.slice(0, 7) ? String(Number(start.slice(8))) : formatDayMonth(start);
  return `${from} – ${formatDayMonth(end)}`;
}

// The count, then the unselected figures in ORDER.
function restList(figures: DayFigures, metric: WeekMetric, sport: SportFilter): string[] {
  return [sportCount(sport, figures.activities), ...ORDER.filter((m) => m !== metric).map((m) => LONG[m](VALUE[m](figures)))];
}

export function dayLine(day: WeekDay, metric: WeekMetric, sport: SportFilter = 'all'): string {
  return [formatWeekdayDay(day.date), LONG[metric](VALUE[metric](day)), ...restList(day, metric, sport)].join(' · ');
}

// Filtered, the Load total names its sport (RUN LOAD); the phone's narrow header drops that prefix, as drawn.
export function totalsText(totals: DayFigures, metric: WeekMetric, current: boolean, sport: SportFilter = 'all', compact = false): { value: string; rest: string } {
  const value = metric === 'load' ? formatWhole(totals.load) : LONG[metric](VALUE[metric](totals));
  const loadHead = sport === 'all' ? 'LOAD' : compact ? '' : `${filterLabel(sport)} LOAD`;
  const head = [metric === 'load' ? loadHead : '', current ? 'SO FAR' : ''].filter(Boolean).join(' ');
  const empty = totals.activities === 0 && ORDER.every((m) => VALUE[m](totals) === 0);
  const others = empty ? [`NO ${pluralNoun(sport)}`] : restList(totals, metric, sport);
  return { value, rest: [head, ...others].filter(Boolean).join(' · ') };
}

// The one line a screen reader hears when the week, metric, sport or selected day changes.
export function weekStatus(
  start: string, days: readonly WeekDay[], metric: WeekMetric, current: boolean, selected: string | null, sport: SportFilter = 'all',
): string {
  const picked = days.find((d) => d.date === selected);
  if (picked !== undefined) return `${weekRangeLabel(start)} · ${dayLine(picked, metric, sport)}`;
  const { value, rest } = totalsText(weekTotals(days), metric, current, sport);
  return `${weekRangeLabel(start)} · ${value} ${rest}`;
}

export interface FormPart { label: string; value: string; delta: string | null; positive: boolean }

const FORM_KEYS = [['FITNESS', 'fitness'], ['FATIGUE', 'fatigue'], ['FORM', 'form']] as const;

export function formParts(end: FitnessValues, previous: FitnessValues | null): FormPart[] {
  return FORM_KEYS.map(([label, key]) => ({
    label,
    value: key === 'form' ? formatSigned(end.form) : formatWhole(end[key]),
    delta: previous === null ? null : `(${formatSigned(end[key] - previous[key])})`,
    positive: key === 'form' && Math.round(end.form) > 0,
  }));
}
